import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Shield, Users, CheckCircle, XCircle, DollarSign, Wallet, Copy, ExternalLink, Link, Settings, Activity, Globe, Code, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BotFeeStatus } from '../components/admin/BotFeeStatus';
import { MembershipCoupons } from '../components/admin/MembershipCoupons';
import { MembershipApprovalButton, MembershipApprovalDialog, MembershipExemptionStatus } from '../components/admin/MembershipApproval';
import { PendingAccounts, PremiumWaiverButton } from '../components/admin/AccountReview';
import { ReferralCodes } from '../components/admin/ReferralCodes';
import { ChartRefreshSettings } from '../components/admin/ChartRefreshSettings';
import { KmsConnectionStatus } from '../components/admin/KmsConnectionStatus';
import { BotExecutionBudget } from '../components/admin/BotExecutionBudget';
import { LocaleText } from '../i18n/Locale';
import '../styles/crypto-market.css';
import { apiError } from '../lib/apiError';
import { usePlatform } from '../contexts/PlatformContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Admin() {
  const { user } = useContext(AuthContext);
  const { refreshStatus } = usePlatform();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [adminFees, setAdminFees] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [masterWallet, setMasterWallet] = useState("");
  const [masterWalletNetwork, setMasterWalletNetwork] = useState("Polygon");
  const [editingWallet, setEditingWallet] = useState(false);
  const [tempWalletAddress, setTempWalletAddress] = useState("");
  
  // Blockchain Management State
  const [connectedWallet, setConnectedWallet] = useState("");
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [contractAddress, setContractAddress] = useState("");
  const [contractDeployed, setContractDeployed] = useState(false);
  const [polygonscanApiKey, setPolygonscanApiKey] = useState("");
  const [deploymentStatus, setDeploymentStatus] = useState("not_started"); // not_started, deploying, deployed, failed
  
  // Platform Settings State
  const [liquidityPoolStatus, setLiquidityPoolStatus] = useState({
    is_operational: false,
    demo_mode: true,
    blockchain_connected: false,
    contract_loaded: false,
    account_loaded: false
  });
  
  // User Management State
  const [allUsers, setAllUsers] = useState([]);
  const [userStats, setUserStats] = useState({
    total_users: null,
    banned_users: null,
    approved_users: null
  });
  const [banReason, setBanReason] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userManagementLoading, setUserManagementLoading] = useState(false);
  const [approvalUser, setApprovalUser] = useState(null);
  const [pendingError, setPendingError] = useState('');

  const refreshPending = async () => {
    setUserManagementLoading(true); setPendingError('');
    try { const { data } = await axios.get(`${API}/admin/pending-users`); setPendingUsers(data); }
    catch (error) { setPendingError(apiError(error)); }
    finally { setUserManagementLoading(false); }
  };

  const onMembershipApproved = (approvedUser) => {
    setAllUsers(users => users.map(item => item.id === approvedUser.id ? { ...item, ...approvedUser } : item));
    setPendingUsers(users => users.filter(item => item.id !== approvedUser.id));
    setApprovalUser(null);
    toast.success(<span data-testid="admin-membership-approval-success">Account approved. Free basic access is now available.</span>);
    fetchAllUsers();
  };

  // Platform Mode State
  const [platformMode, setPlatformMode] = useState(null);
  const [modeError, setModeError] = useState('');
  const [confirmLive, setConfirmLive] = useState(false);
  const modeRequest = useRef(0);
  const liquidityPoolDemoMode = platformMode === 'demo';
  const [maintenanceMessage, setMaintenanceMessage] = useState("Platform is under maintenance. Please check back later.");
  const [platformModeLoading, setPlatformModeLoading] = useState(false);
  const [availableModes, setAvailableModes] = useState({});

  useEffect(() => {
    fetchData();
    fetchPlatformMode();
    checkMetaMaskConnection();
  }, []);

  useEffect(() => {
    const refresh = () => { if (!platformModeLoading) fetchPlatformMode(); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [platformModeLoading]);

  const checkMetaMaskConnection = async () => {
    try {
      if (window.ethereum) {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setConnectedWallet(accounts[0]);
          setIsWalletConnected(true);
        }
      }
    } catch (error) {
      console.error("Error checking MetaMask connection:", error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setUserManagementLoading(true);
      const response = await axios.get(`${API}/admin/users`);
      setAllUsers(response.data.users || []);
      setUserStats({ total_users: response.data.total_users, banned_users: response.data.banned_users,
        approved_users: response.data.approved_users });
    } catch (error) {
      console.error("Error fetching all users:", error);
      setUserStats({ total_users: null, banned_users: null, approved_users: null });
      toast.error("Failed to fetch user data");
    } finally {
      setUserManagementLoading(false);
    }
  };

  const banUser = async (userEmail, reason) => {
    setUserManagementLoading(true);
    try {
      const response = await axios.post(`${API}/admin/ban-user`, null, {
        params: {
          user_email: userEmail,
          ban_reason: reason || "Violation of terms of service"
        }
      });
      
      if (response.data.success) {
        toast.success(`User ${userEmail} has been banned`);
        await fetchAllUsers(); // Refresh the list
        setSelectedUser(null);
        setBanReason("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to ban user");
    } finally {
      setUserManagementLoading(false);
    }
  };

  const unbanUser = async (userEmail) => {
    setUserManagementLoading(true);
    try {
      const response = await axios.post(`${API}/admin/unban-user`, null, {
        params: {
          user_email: userEmail
        }
      });
      
      if (response.data.success) {
        toast.success(`User ${userEmail} has been unbanned`);
        await fetchAllUsers(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unban user");
    } finally {
      setUserManagementLoading(false);
    }
  };

  const deleteUser = async (userEmail) => {
    // Confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete user ${userEmail}?\n\nFuture membership renewals will be stopped first. No refund will be issued.\n\nThis action cannot be undone and will remove:\n- User account\n- All trading history\n- All wallet data\n- All related records`
    );
    
    if (!confirmDelete) return;

    setUserManagementLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/force-delete-user`, {
        params: {
          email: userEmail
        }
      });
      
      if (response.data.success) {
        toast.success(
          <div>
            <div className="font-semibold">User Deleted Successfully</div>
            <div className="text-sm">User: {userEmail}</div>
            <div className="text-sm">Records deleted: {Object.values(response.data.deleted_records).reduce((a, b) => a + b, 0)}</div>
          </div>
        );
        await fetchAllUsers(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setUserManagementLoading(false);
    }
  };

  const fetchPlatformMode = async () => {
    const requestId = ++modeRequest.current;
    try {
      const response = await axios.get(`${API}/admin/platform-mode`);
      if (requestId !== modeRequest.current) return;
      setPlatformMode(response.data.mode);
      setModeError('');
      setMaintenanceMessage(response.data.maintenance_message || "Platform is under maintenance. Please check back later.");
      setAvailableModes(response.data.available_modes || {});
    } catch (error) {
      console.error("Error fetching platform mode:", error);
      if (requestId !== modeRequest.current) return;
      setPlatformMode(null);
      setModeError('Platform mode could not be confirmed. Refresh to retry.');
    }
  };

  const setPlatformModeHandler = async (newMode) => {
    setPlatformModeLoading(true);
    ++modeRequest.current;
    try {
      const response = await axios.post(`${API}/admin/platform-mode`, null, {
        params: {
          mode: newMode,
          maintenance_message: maintenanceMessage
        }
      });
      
      if (response.data.success) {
        ++modeRequest.current;
        setPlatformMode(response.data.mode);
        setAvailableModes(response.data.available_modes || {});
        setModeError('');
        setConfirmLive(false);
        await refreshStatus();
        await fetchLiquidityPoolStatus();
        toast.success(
          <div data-testid="admin-mode-save-success">
            <div className="font-semibold">Platform Mode Updated</div>
            <div className="text-sm">Mode: {response.data.mode.toUpperCase()}</div>
            <div className="text-sm">{response.data.message}</div>
          </div>
        );
      }
    } catch (error) {
      await fetchPlatformMode();
      await refreshStatus();
      toast.error(<span data-testid="admin-mode-save-error">{apiError(error)}</span>);
    } finally {
      setPlatformModeLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      // Try to fetch pending users to check if admin
      const response = await axios.get(`${API}/admin/pending-users`);
      setPendingUsers(response.data);
      setIsAdmin(true);
      
      // Fetch admin fees
      const feesResponse = await axios.get(`${API}/admin/fees`);
      setAdminFees(feesResponse.data.total_fees);
      
      // Fetch master wallet
      const walletResponse = await axios.get(`${API}/admin/master-wallet`);
      setMasterWallet(walletResponse.data.wallet_address);
      setMasterWalletNetwork(walletResponse.data.network);
      setTempWalletAddress(walletResponse.data.wallet_address);

      // Fetch contract deployment status
      const contractResponse = await axios.get(`${API}/admin/contract-status`);
      if (contractResponse.data.deployed) {
        setContractDeployed(true);
        setContractAddress(contractResponse.data.contract_address);
        setDeploymentStatus('deployed');
      }
      
      // Fetch liquidity pool status
      await fetchLiquidityPoolStatus();
      
      // Fetch all users for user management
      await fetchAllUsers();
      
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
        toast.error("Admin access required");
      }
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMasterWallet = async () => {
    try {
      await axios.post(`${API}/admin/master-wallet?wallet_address=${tempWalletAddress}&network=${masterWalletNetwork}`);
      setMasterWallet(tempWalletAddress);
      setEditingWallet(false);
      toast.success("Master wallet updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update master wallet");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // MetaMask Functions
  const connectMetaMask = async () => {
    try {
      if (!window.ethereum) {
        toast.error("MetaMask is not installed. Please install MetaMask extension.");
        return;
      }

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (accounts.length > 0) {
        setConnectedWallet(accounts[0]);
        setIsWalletConnected(true);
        toast.success("MetaMask connected successfully!");
        
        // Check if we're on Polygon network
        const chainId = await window.ethereum.request({ method: 'eth_chainId' });
        if (chainId !== '0x89') { // Polygon mainnet
          await switchToPolygon();
        }
      }
    } catch (error) {
      console.error("MetaMask connection error:", error);
      toast.error("Failed to connect MetaMask: " + error.message);
    }
  };

  const switchToPolygon = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }], // Polygon Mainnet
      });
      toast.success("Switched to Polygon network");
    } catch (switchError) {
      // If network doesn't exist, add it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: '0x89',
              chainName: 'Polygon Mainnet',
              nativeCurrency: {
                name: 'MATIC',
                symbol: 'MATIC',
                decimals: 18
              },
              rpcUrls: ['https://polygon-rpc.com/'],
              blockExplorerUrls: ['https://polygonscan.com/']
            }]
          });
          toast.success("Polygon network added and switched");
        } catch (addError) {
          console.error('Failed to add Polygon network:', addError);
          toast.error("Failed to add Polygon network");
        }
      } else {
        console.error('Failed to switch to Polygon network:', switchError);
        toast.error("Failed to switch to Polygon network");
      }
    }
  };

  const getDeploymentStatusText = () => {
    switch (deploymentStatus) {
      case 'not_started': return 'Not deployed';
      case 'deploying': return 'Deploying...';
      case 'deployed': return 'Deployed';
      case 'failed': return 'Deployment failed';
      default: return 'Unknown status';
    }
  };

  const savePolygonscanApiKey = async () => {
    try {
      await axios.post(`${API}/admin/polygonscan-api-key`, {
        api_key: polygonscanApiKey
      });
      toast.success("PolygonScan API key saved successfully!");
    } catch (error) {
      console.error("Failed to save API key:", error);
      toast.error("Failed to save API key: " + (error.response?.data?.detail || error.message));
    }
  };

  const deployContract = async () => {
    try {
      setDeploymentStatus('deploying');
      toast.info("Starting contract deployment...");

      const response = await axios.post(`${API}/admin/deploy-contract`, {
        wallet_address: connectedWallet,
        polygonscan_api_key: polygonscanApiKey || "" // Optional
      });

      if (response.data.success) {
        setContractAddress(response.data.contract_address);
        setContractDeployed(true);
        setDeploymentStatus('deployed');
        toast.success("Contract deployed successfully!");
      } else {
        throw new Error(response.data.error || "Deployment failed");
      }
    } catch (error) {
      console.error("Contract deployment error:", error);
      setDeploymentStatus('failed');
      toast.error("Contract deployment failed: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reject ${userName}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/reject-user/${userId}`);
      toast.success(`${userName} rejected`);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject user");
    }
  };

  const fetchLiquidityPoolStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/liquidity-status`);
      setLiquidityPoolStatus(response.data);
    } catch (error) {
      console.error("Error fetching liquidity pool status:", error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout>
        <Card className="bg-slate-800/50 border-slate-700 p-12 text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-100 mb-2">Access Denied</h2>
          <p className="text-slate-400">You do not have administrator privileges.</p>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-container">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2 flex items-center">
            <Shield className="w-8 h-8 mr-3 text-cyan-400" />
            Admin Dashboard
          </h1>
          <p className="text-slate-400">Manage memberships, coupons and platform settings</p>
        </div>

        <KmsConnectionStatus key={user?.id} />
        <a href="/bot-pilot" className="inline-flex items-center gap-2 text-sm text-teal-200 underline underline-offset-4" data-testid="admin-bot-pilot-link">Bot pilot · Owner-only evaluation →</a>

        {/* Platform Mode Control - Priority Section */}
        <Card className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-purple-800/50 p-5 sm:p-8" data-testid="admin-platform-mode-control">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Settings className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-100">Platform Mode Control</h2>
                <p className="text-slate-400 text-sm">Control platform operational mode</p>
              </div>
            </div>
            <div data-testid="admin-platform-mode-badge" className={`px-4 py-2 rounded-full text-sm font-semibold ${
              platformMode === 'live' ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/50' :
              platformMode === 'demo' ? 'bg-blue-600/20 text-blue-400 border border-blue-600/50' :
              'bg-amber-600/20 text-amber-400 border border-amber-600/50'
            }`}>
              {platformMode ? `${platformMode.toUpperCase()} MODE` : 'MODE UNAVAILABLE'}
            </div>
          </div>

          {/* Mode Description */}
          <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <h3 className="text-slate-200 font-semibold mb-2" data-testid="admin-current-mode">Current Mode: <LocaleText value={platformMode ? platformMode.charAt(0).toUpperCase() + platformMode.slice(1) : 'Unavailable'} /></h3>
            <p className="text-slate-400 text-sm" data-testid="admin-mode-description">
              <LocaleText value={availableModes[platformMode] || 'Waiting for confirmed platform status.'} />
            </p>
            {platformMode === 'demo' && (
              <div className="mt-3 p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                <p className="text-blue-300 text-sm" data-testid="admin-demo-safety-notice">
                  <strong>Whole-platform Demo:</strong> New real-money orders are blocked. Existing balances and research data are not converted to demo funds. Billing settings are unchanged.
                </p>
              </div>
            )}
          </div>

          {/* Mode Buttons */}
          {modeError && <p role="alert" className="text-amber-300 mb-4 text-sm" data-testid="admin-mode-load-error">{modeError}</p>}
          <Button variant="outline" className="mb-4" disabled={platformModeLoading} data-testid="admin-mode-refresh" onClick={async () => { await fetchPlatformMode(); await refreshStatus(); await fetchLiquidityPoolStatus(); }}>Refresh mode</Button>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Button
              data-testid="admin-mode-live"
              onClick={() => setConfirmLive(true)}
              disabled={platformModeLoading || !platformMode || platformMode === 'live'}
              className={`p-6 h-auto flex-col space-y-2 ${
                platformMode === 'live' 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-slate-700 hover:bg-emerald-600/20 border border-emerald-600/30 text-slate-200'
              }`}
            >
              <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Globe className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="text-center">
                <div className="font-semibold">Live Mode</div>
                <div className="text-xs opacity-75">Existing safeguards still apply</div>
              </div>
            </Button>

            <Button
              data-testid="admin-mode-demo"
              onClick={() => setPlatformModeHandler('demo')}
              disabled={platformModeLoading || !platformMode || platformMode === 'demo'}
              className={`p-6 h-auto flex-col space-y-2 ${
                platformMode === 'demo' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-700 hover:bg-blue-600/20 border border-blue-600/30 text-slate-200'
              }`}
            >
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Code className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-center">
                <div className="font-semibold">Demo Mode</div>
                <div className="text-xs opacity-75">Whole platform · No new real orders</div>
              </div>
            </Button>

            <Button
              data-testid="admin-mode-maintenance"
              onClick={() => setPlatformModeHandler('maintenance')}
              disabled={platformModeLoading || !platformMode || platformMode === 'maintenance'}
              className={`p-6 h-auto flex-col space-y-2 ${
                platformMode === 'maintenance' 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-slate-700 hover:bg-amber-600/20 border border-amber-600/30 text-slate-200'
              }`}
            >
              <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
              </div>
              <div className="text-center">
                <div className="font-semibold">Maintenance</div>
                <div className="text-xs opacity-75">Platform offline</div>
              </div>
            </Button>
          </div>

          {/* Maintenance Message Input */}
          {platformMode === 'maintenance' && (
            <div className="mt-4 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <Label htmlFor="maintenance-message" className="text-amber-200 text-sm font-semibold">
                Maintenance Message
              </Label>
              <Input
                id="maintenance-message"
                data-testid="admin-maintenance-message"
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="Enter maintenance message for users"
                className="mt-2 bg-slate-700 border-amber-600/50 text-slate-100"
              />
            </div>
          )}

          {platformModeLoading && (
            <div className="flex items-center justify-center py-4" role="status" data-testid="admin-mode-saving">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400"></div>
              <span className="ml-2 text-slate-400">Updating platform mode...</span>
            </div>
          )}
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-cyan-800/50 p-6">
            <div className="flex items-center space-x-3">
              <DollarSign className="w-10 h-10 text-cyan-400" />
              <div>
                <p className="text-slate-400 text-sm" data-testid="admin-overview-fee-label">Recorded Fee Ledger</p>
                <p className="text-2xl font-bold text-cyan-400" data-testid="admin-overview-fee-amount">
                  ${adminFees.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="flex items-center space-x-3">
              <Users className="w-10 h-10 text-yellow-400" />
              <div>
                <p className="text-slate-400 text-sm">Pending Approvals</p>
                <p className="text-2xl font-bold text-yellow-400" data-testid="admin-pending-users-count">{pendingUsers.length}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
              <div>
                <p className="text-slate-400 text-sm">Platform Status</p>
                <p className="text-xl font-bold text-emerald-400" data-testid="admin-overview-mode">{platformMode || 'Unavailable'}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-6">
            <div className="flex items-center space-x-3">
              {liquidityPoolStatus.is_operational ? (
                <Activity className="w-10 h-10 text-blue-400" />
              ) : (
                <XCircle className="w-10 h-10 text-red-400" />
              )}
              <div>
                <p className="text-slate-400 text-sm">Liquidity Pool</p>
                <p className={`text-xl font-bold ${
                  liquidityPoolStatus.is_operational ? 'text-blue-400' : 'text-red-400'
                }`}>
                  {platformMode === 'demo' ? 'Demo Mode' : platformMode === 'maintenance' ? 'Maintenance' : !platformMode ? 'Unavailable' : liquidityPoolStatus.is_operational ? 'Operational' : 'Offline'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <PendingAccounts users={pendingUsers} loading={userManagementLoading} error={pendingError}
          onRefresh={refreshPending} onApprove={setApprovalUser} onReject={handleReject} />
        <ReferralCodes />

        {/* User Management Section */}
        <Card className="bg-gradient-to-br from-red-900/30 to-orange-900/30 border-red-800/50 p-5 sm:p-8" data-testid="admin-user-management">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-100">User Management</h2>
                <p className="text-slate-400 text-sm">Membership approvals and account access</p>
              </div>
            </div>
          </div>

          {/* User Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-slate-800/50 border-slate-700 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-100" data-testid="admin-total-users">{userStats.total_users ?? 'Unavailable'}</div>
                <div className="text-slate-400 text-sm">Total Users</div>
              </div>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400" data-testid="admin-approved-users">{userStats.approved_users ?? 'Unavailable'}</div>
                <div className="text-slate-400 text-sm">Approved</div>
              </div>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700 p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-400" data-testid="admin-banned-users">{userStats.banned_users ?? 'Unavailable'}</div>
                <div className="text-slate-400 text-sm">Banned</div>
              </div>
            </Card>
          </div>

          {/* User List */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-100">All Users</h3>
            <div className="max-h-96 overflow-y-auto space-y-3">
              {allUsers.map((userData, index) => (
                <Card key={userData.id || index} className="bg-slate-800/50 border-slate-700 p-4 min-w-0" data-testid={`admin-user-row-${userData.id || index}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 min-w-0">
                    <div className="flex items-center gap-4 min-w-0">
                      <Avatar className="w-10 h-10 shrink-0">
                        <AvatarFallback className="bg-slate-600 text-slate-200">
                          {userData.name?.charAt(0)?.toUpperCase() || userData.username?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 [overflow-wrap:anywhere]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-100" data-testid={`admin-user-name-${userData.id || index}`}>
                            {userData.name || userData.username}
                          </span>
                          {userData.banned && (
                            <Badge variant="destructive" className="bg-red-600/20 text-red-400 border-red-600/50" data-testid={`admin-user-status-${userData.id || index}`}>
                              Banned
                            </Badge>
                          )}
                          {!userData.approved && !userData.banned && (
                            <Badge variant="secondary" className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50" data-testid={`admin-user-status-${userData.id || index}`}>
                              Pending
                            </Badge>
                          )}
                          {userData.approved && !userData.banned && (
                            <Badge variant="default" className="bg-emerald-600/20 text-emerald-400 border-emerald-600/50" data-testid={`admin-user-status-${userData.id || index}`}>
                              Active
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-400" data-testid={`admin-user-email-${userData.id || index}`}>{userData.email}</div>
                        <MembershipExemptionStatus user={userData} />
                        {userData.referral && <p className="text-xs text-slate-400 mt-1" data-testid={`admin-user-referral-${userData.id}`}>Referral: {userData.referral.label} · {userData.referral.code_hint}</p>}
                        {userData.account_approved_at && <p className="text-xs text-slate-400 mt-1" data-testid={`admin-account-approval-audit-${userData.id}`}>Account approved by {userData.account_approved_by_name} · {new Date(userData.account_approved_at).toLocaleString()}</p>}
                        {userData.banned && userData.ban_reason && (
                          <div className="text-xs text-red-400 mt-1" data-testid={`admin-ban-reason-${userData.id}`}>
                            Reason: {userData.ban_reason}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap shrink-0 max-w-full self-start lg:self-auto">
                      <MembershipApprovalButton user={userData} onApprove={setApprovalUser} disabled={userManagementLoading} />
                      <PremiumWaiverButton user={userData} onSaved={fetchAllUsers} />
                      {userData.banned ? (
                        <Button
                          onClick={() => unbanUser(userData.email)}
                          data-testid={`admin-unban-user-${userData.id || index}`}
                          disabled={userManagementLoading}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Unban
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setSelectedUser(userData)}
                          data-testid={`admin-open-ban-${userData.id || index}`}
                          disabled={userManagementLoading}
                          size="sm"
                          variant="destructive"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Ban User
                        </Button>
                      )}
                      <Button
                        onClick={() => deleteUser(userData.email)}
                        data-testid={`admin-delete-user-${userData.id || index}`}
                        disabled={userManagementLoading}
                        size="sm"
                        variant="destructive"
                        className="bg-red-800 hover:bg-red-900 text-white"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              {allUsers.length === 0 && (
                <div className="text-center text-slate-400 py-8">
                  No users found
                </div>
              )}
            </div>
          </div>

          {/* Ban User Modal */}
          {selectedUser && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="admin-ban-user-dialog">
              <Card className="bg-slate-800 border-slate-700 p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-bold text-slate-100 mb-4">
                  Ban User: {selectedUser.name || selectedUser.username}
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="ban-reason" className="text-slate-300">
                      Ban Reason
                    </Label>
                    <Input
                      id="ban-reason"
                      data-testid="admin-ban-user-reason-input"
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      placeholder="Enter reason for banning this user"
                      className="bg-slate-700 border-slate-600 text-slate-100"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button
                      variant="outline"
                      data-testid="admin-ban-user-cancel"
                      onClick={() => {
                        setSelectedUser(null);
                        setBanReason("");
                      }}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => banUser(selectedUser.email, banReason)}
                      data-testid="admin-ban-user-confirm"
                      disabled={userManagementLoading}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {userManagementLoading ? "Banning..." : "Ban User"}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </Card>

        {/* Blockchain Management Section */}
        <Card className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-800/50 p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Link className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-100">Blockchain Management</h2>
                <p className="text-slate-400 text-sm">MetaMask integration and smart contract deployment</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* MetaMask Connection */}
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Wallet className="w-8 h-8 text-orange-400" />
                  <div>
                    <p className="text-slate-300 font-semibold">MetaMask Wallet</p>
                    <p className="text-slate-500 text-sm">Connect admin wallet</p>
                  </div>
                </div>
              </div>
              
              {isWalletConnected ? (
                <div className="space-y-3">
                  <div className="bg-emerald-900/20 border border-emerald-800/50 rounded-lg p-3">
                    <p className="text-emerald-200 text-sm font-medium">✅ Connected</p>
                  </div>
                  <div className="font-mono text-xs text-slate-300 bg-slate-900 p-3 rounded break-all">
                    {connectedWallet}
                  </div>
                  <Button 
                    onClick={() => setIsWalletConnected(false)}
                    variant="outline"
                    size="sm"
                    className="w-full bg-slate-700 hover:bg-slate-600 border-slate-600"
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-3">
                    <p className="text-yellow-200 text-sm">⚠️ Not Connected</p>
                  </div>
                  <Button 
                    onClick={connectMetaMask}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white border-0"
                  >
                    Connect MetaMask
                  </Button>
                </div>
              )}
            </Card>

            {/* Contract Status */}
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Settings className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-slate-300 font-semibold">Smart Contract</p>
                    <p className="text-slate-500 text-sm">Liquidity Pool deployment</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                {contractDeployed ? (
                  <div>
                    <div className="bg-emerald-900/20 border border-emerald-800/50 rounded-lg p-3 mb-3">
                      <p className="text-emerald-200 text-sm font-medium">✅ Deployed</p>
                    </div>
                    <div className="font-mono text-xs text-slate-300 bg-slate-900 p-3 rounded break-all">
                      {contractAddress}
                    </div>
                    <Button 
                      onClick={() => window.open(`https://polygonscan.com/address/${contractAddress}`, '_blank')}
                      variant="outline"
                      size="sm"
                      className="w-full bg-slate-700 hover:bg-slate-600 border-slate-600"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View on PolygonScan
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-3 mb-3">
                      <p className="text-yellow-200 text-sm">⏳ {getDeploymentStatusText()}</p>
                    </div>
                    <Button 
                      onClick={deployContract}
                      disabled={deploymentStatus === 'deploying' || !isWalletConnected}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white border-0 disabled:opacity-50"
                    >
                      {deploymentStatus === 'deploying' ? (
                        <>
                          <Activity className="w-4 h-4 mr-2 animate-spin" />
                          Deploying...
                        </>
                      ) : (
                        'Deploy Contract'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* PolygonScan API Key */}
          <Card className="bg-slate-800/50 border-slate-700 p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <ExternalLink className="w-6 h-6 text-cyan-400" />
              <div>
                <p className="text-slate-300 font-semibold">PolygonScan API Key</p>
                <p className="text-slate-500 text-sm">Required for contract verification</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <Input
                type="password"
                value={polygonscanApiKey}
                onChange={(e) => setPolygonscanApiKey(e.target.value)}
                placeholder="Enter your PolygonScan API key..."
                className="bg-slate-700 border-slate-600 text-slate-100 font-mono"
              />
              <div className="bg-cyan-900/20 border border-cyan-800/50 rounded-lg p-3">
                <p className="text-cyan-200 text-sm">
                  💡 Get your API key from <a href="https://polygonscan.com/apis" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">polygonscan.com/apis</a>
                </p>
              </div>
              <Button 
                onClick={savePolygonscanApiKey}
                disabled={!polygonscanApiKey.trim()}
                className="bg-cyan-600 hover:bg-cyan-700 text-white border-0 disabled:opacity-50"
              >
                Save API Key
              </Button>
            </div>
          </Card>

          {/* Deployment Instructions */}
          {!contractDeployed && (
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Deployment Steps</h3>
              <div className="space-y-2 text-sm text-slate-300">
                <div className={`flex items-center space-x-2 ${polygonscanApiKey ? 'text-emerald-400' : 'text-yellow-400'}`}>
                  <div className={`w-4 h-4 rounded-full ${polygonscanApiKey ? 'bg-emerald-500' : 'bg-yellow-500'} flex items-center justify-center`}>
                    {polygonscanApiKey ? <CheckCircle className="w-3 h-3 text-white" /> : <span className="text-xs text-black">?</span>}
                  </div>
                  <span>1. Add PolygonScan API Key (Optional)</span>
                </div>
                <div className={`flex items-center space-x-2 ${isWalletConnected ? 'text-emerald-400' : ''}`}>
                  <div className={`w-4 h-4 rounded-full ${isWalletConnected ? 'bg-emerald-500' : 'bg-slate-600'} flex items-center justify-center`}>
                    {isWalletConnected && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span>2. Connect MetaMask Wallet</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-slate-600"></div>
                  <span>3. Deploy Liquidity Pool Contract</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-slate-600"></div>
                  <span>4. Fund Contract with Initial USDT</span>
                </div>
              </div>
            </Card>
          )}
        </Card>

        {/* Master Wallet Section */}
        <Card className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border-purple-800/50 p-5 sm:p-8" data-testid="admin-master-wallet-section">
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-base md:text-lg font-bold text-slate-100" data-testid="admin-master-wallet-title">Master Wallet</h2>
                <p className="text-slate-400 text-sm" data-testid="admin-master-wallet-purpose">Configured destination · automatic collection is not enabled</p>
              </div>
            </div>
            {!editingWallet && (
              <Button
                onClick={() => setEditingWallet(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white border-0"
                data-testid="edit-wallet-button"
              >
                Edit Wallet
              </Button>
            )}
          </div>

          {/* Current Balance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <div className="flex items-center space-x-3">
                <DollarSign className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-slate-400 text-sm" data-testid="admin-fee-ledger-label">Recorded fee ledger · not wallet funds</p>
                  <p className="text-xl font-bold text-emerald-400 break-words" data-testid="admin-fee-ledger-amount">
                    ${adminFees.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} USDT
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <div className="flex items-center space-x-3">
                <Wallet className="w-8 h-8 text-cyan-400" />
                <div>
                  <p className="text-slate-400 text-sm">Network</p>
                  <p className="text-xl font-bold text-cyan-400" data-testid="admin-master-wallet-network">{masterWalletNetwork}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Wallet Address Display/Edit */}
          {!editingWallet ? (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300 mb-2 block">Master Wallet Address</Label>
                {masterWallet ? (
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <div className="flex-1 min-w-0 basis-full sm:basis-auto bg-slate-800 border border-slate-700 rounded-lg p-4 font-mono text-sm text-slate-100 break-all" data-testid="admin-master-wallet-address">
                      {masterWallet}
                    </div>
                    <Button
                      onClick={() => copyToClipboard(masterWallet)}
                      variant="outline"
                      className="bg-slate-700 hover:bg-slate-600 border-slate-600"
                      data-testid="copy-wallet-button"
                      title="Copy master wallet address"
                      aria-label="Copy master wallet address"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => window.open(`https://polygonscan.com/address/${masterWallet}`, '_blank')}
                      variant="outline"
                      className="bg-slate-700 hover:bg-slate-600 border-slate-600"
                      data-testid="view-polygonscan-button"
                      title="View master wallet on PolygonScan"
                      aria-label="View master wallet on PolygonScan"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-4">
                    <p className="text-yellow-200 text-sm" data-testid="admin-master-wallet-missing">
                      No master wallet is configured. A destination must be verified before any future collection is enabled.
                    </p>
                  </div>
                )}
              </div>

              {masterWallet && (
                <div className="bg-cyan-900/20 border border-cyan-800/50 rounded-lg p-4">
                  <p className="text-cyan-200 text-sm" data-testid="admin-master-wallet-transfer-notice">
                    No automatic deposits are verified. The internal fee ledger above is not a withdrawable on-chain balance; saving this {masterWalletNetwork} address does not transfer funds.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300 mb-2 block">Wallet Address</Label>
                <Input
                  value={tempWalletAddress}
                  onChange={(e) => setTempWalletAddress(e.target.value)}
                  placeholder="0x..."
                  className="bg-slate-700 border-slate-600 text-slate-100 font-mono"
                  data-testid="wallet-address-input"
                />
                <p className="text-slate-500 text-xs mt-1">Enter your {masterWalletNetwork} wallet address (must start with 0x)</p>
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={handleUpdateMasterWallet}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                  data-testid="save-wallet-button"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Wallet
                </Button>
                <Button
                  onClick={() => {
                    setTempWalletAddress(masterWallet);
                    setEditingWallet(false);
                  }}
                  variant="outline"
                  className="bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-300"
                  data-testid="cancel-wallet-button"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <BotFeeStatus destination={masterWallet} />
        </Card>

        <MembershipCoupons />

        {/* Platform Settings Section */}
        <ChartRefreshSettings />
        <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-800/50 p-5 sm:p-8" data-testid="admin-platform-settings">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Settings className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-100">Platform Settings</h2>
                <p className="text-slate-400 text-sm">Configure platform features and trading options</p>
              </div>
            </div>
          </div>

          <BotExecutionBudget key={user?.id} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Liquidity Pool Demo Mode Control */}
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Activity className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-slate-300 font-semibold">Platform & Liquidity Pool Mode</p>
                    <p className="text-slate-400 text-sm" data-testid="admin-pool-mode-description">
                      {liquidityPoolDemoMode ? 'Demo data · Real orders blocked' : platformMode === 'live' ? 'Live selected · Connectivity checked separately' : platformMode === 'maintenance' ? 'Maintenance · New orders blocked' : 'Mode unavailable'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className={`border rounded-lg p-3 ${
                  liquidityPoolDemoMode 
                    ? 'bg-amber-900/20 border-amber-800/50' 
                    : 'bg-emerald-900/20 border-emerald-800/50'
                }`}>
                  <p data-testid="admin-pool-mode-status" className={`text-sm font-medium ${
                    liquidityPoolDemoMode ? 'text-amber-200' : 'text-emerald-200'
                  }`}>
                    {liquidityPoolDemoMode ? 'Demo Mode Active · Whole Platform' : platformMode === 'live' ? 'Live Mode Selected · Whole Platform' : platformMode === 'maintenance' ? 'Maintenance Mode · Whole Platform' : 'Mode unavailable'}
                  </p>
                </div>
                
                <Button 
                  data-testid="admin-pool-use-demo"
                  onClick={() => setPlatformModeHandler('demo')}
                  disabled={platformModeLoading || !platformMode || platformMode === 'demo'}
                  className={`w-full ${
                    liquidityPoolDemoMode 
                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                      : 'bg-amber-600 hover:bg-amber-700'
                  } text-white border-0`}
                >
                  {platformModeLoading ? 'Updating...' : liquidityPoolDemoMode ? 'Whole-platform Demo active' : 'Use Demo across platform'}
                </Button>
              </div>
            </Card>

            {/* Options Trading Toggle */}
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <DollarSign className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-slate-300 font-semibold">Options Trading</p>
                    <p className="text-slate-400 text-sm" data-testid="admin-options-mode-description">
                      Readiness review only · No new orders
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="border rounded-lg p-3 bg-amber-900/20 border-amber-800/50">
                  <p className="text-sm font-medium text-amber-200" data-testid="admin-options-locked-status">
                    Options trading remains locked
                  </p>
                </div>
                
                <Button 
                  data-testid="admin-options-unavailable"
                  disabled
                  className="w-full bg-slate-700 text-white border-0"
                >
                  Not available
                </Button>
              </div>
            </Card>
          </div>

          {/* Liquidity Pool Detailed Status */}
          <Card className="bg-slate-800/50 border-slate-700 p-6 mt-6">
            <div className="flex items-center space-x-3 mb-4">
              <Activity className="w-6 h-6 text-blue-400" />
              <h3 className="text-lg font-semibold text-slate-100">Liquidity Pool Status Details</h3>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
                  liquidityPoolStatus.blockchain_connected ? 'bg-emerald-400' : 'bg-red-400'
                }`}></div>
                <p className="text-xs text-slate-400">Blockchain</p>
                <p className={`text-sm font-medium ${
                  liquidityPoolStatus.blockchain_connected ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {liquidityPoolStatus.blockchain_connected ? 'Connected' : 'Disconnected'}
                </p>
              </div>
              
              <div className="text-center">
                <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
                  liquidityPoolStatus.contract_loaded ? 'bg-emerald-400' : 'bg-red-400'
                }`}></div>
                <p className="text-xs text-slate-400">Smart Contract</p>
                <p className={`text-sm font-medium ${
                  liquidityPoolStatus.contract_loaded ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {liquidityPoolStatus.contract_loaded ? 'Loaded' : 'Not Loaded'}
                </p>
              </div>
              
              <div className="text-center">
                <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
                  liquidityPoolStatus.account_loaded ? 'bg-emerald-400' : 'bg-red-400'
                }`}></div>
                <p className="text-xs text-slate-400">Wallet Account</p>
                <p className={`text-sm font-medium ${
                  liquidityPoolStatus.account_loaded ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {liquidityPoolStatus.account_loaded ? 'Loaded' : 'Not Loaded'}
                </p>
              </div>
              
              <div className="text-center">
                <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
                  liquidityPoolStatus.is_operational ? 'bg-emerald-400' : 'bg-amber-400'
                }`}></div>
                <p className="text-xs text-slate-400">Overall Status</p>
                <p className={`text-sm font-medium ${
                  liquidityPoolStatus.is_operational ? 'text-emerald-400' : 'text-amber-400'
                }`}>
                  {platformMode === 'demo' ? 'Demo' : platformMode === 'maintenance' ? 'Maintenance' : !platformMode ? 'Unavailable' : liquidityPoolStatus.is_operational ? 'Operational' : 'Limited'}
                </p>
              </div>
            </div>
          </Card>
        </Card>

        <Dialog open={confirmLive} onOpenChange={setConfirmLive}>
          <DialogContent data-testid="admin-live-mode-confirmation" closeTestId="admin-live-mode-close">
            <DialogHeader><DialogTitle>Switch the whole platform to Live?</DialogTitle><DialogDescription>New user-signed real-money orders may become available where already configured. This does not unlock autonomous bots, options trading or other disabled features. Existing permissions and spending limits remain in force.</DialogDescription></DialogHeader>
            <div className="flex flex-wrap gap-3"><Button variant="outline" data-testid="admin-live-mode-cancel" disabled={platformModeLoading} onClick={() => setConfirmLive(false)}>Cancel</Button><Button data-testid="admin-live-mode-confirm" disabled={platformModeLoading} onClick={() => setPlatformModeHandler('live')}>Confirm Live mode</Button></div>
          </DialogContent>
        </Dialog>
        {approvalUser && <MembershipApprovalDialog key={approvalUser.id} user={approvalUser}
          onClose={() => setApprovalUser(null)} onApproved={onMembershipApproved} />}
      </div>
    </Layout>
  );
}

export default Admin;
